import { Component } from '@verndale/core';
import Splide from '@splidejs/splide';
import '@splidejs/splide/css';
import { animate, stagger, inView } from 'motion';
import '../../scss/modules/_news-events-card-row.scss';

class NewsEventsCardRow extends Component {
  private splide!: Splide;
  private isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(el: HTMLElement) {
    super(el);

    this.setupDefaults_();
    this.addListeners_();
  }

  setupDefaults_() {
    this.dom = {
      el: this.el,
      splideEl: this.el.querySelector('.splide') as HTMLElement,
      paginationPrev: this.el.querySelector(
        '.news-events-card-row__pagination-prev'
      ) as HTMLButtonElement,
      paginationNext: this.el.querySelector(
        '.news-events-card-row__pagination-next'
      ) as HTMLButtonElement,
      paginationCurrent: this.el.querySelector(
        '.news-events-card-row__pagination-current'
      ) as HTMLElement,
      paginationTotal: this.el.querySelector(
        '.news-events-card-row__pagination-total'
      ) as HTMLElement,
      title: this.el.querySelector<HTMLElement>('.news-events-card-row__title')
    };

    this.initSplide();
    this.animateTitle();
  }

  addListeners_() {
    const { paginationPrev, paginationNext } = this.dom;

    (paginationPrev as HTMLButtonElement)?.addEventListener('click', () => this.splide.go('<'));
    (paginationNext as HTMLButtonElement)?.addEventListener('click', () => this.splide.go('>'));
  }

  initSplide() {
    const splideEl = this.dom.splideEl as HTMLElement;
    if (!splideEl) return;

    const splide = new Splide(splideEl, {
      pagination: false,
      arrows: false,
      gap: '24px',
      perPage: 3,
      rewind: true,
      breakpoints: {
        1279: {
          perPage: 1
        }
      }
    });

    splide.on('mounted', () => {
      const totalPages = Math.ceil(splide.length / (splide.options.perPage ?? 1));
      const slides = Array.from(splideEl.querySelectorAll<HTMLElement>('.splide__slide'));
      const cards = slides
        .map(slide => slide.querySelector<HTMLElement>('.news-event-card'))
        .filter(Boolean) as HTMLElement[];

      this.updateCurrentPage(splide.index);
      this.updateTotalPages(totalPages);

      // Set initial state before observing
      cards.forEach(card => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
      });

      // Animate cards when the component enters the viewport
      inView(
        this.el,
        () => {
          animate(
            cards,
            { opacity: [0, 1], transform: ['translateY(50px)', 'translateY(0)'] },
            { duration: 0.75, delay: stagger(0.15) }
          );
        },
        { amount: 0.75 } // Trigger when 75% of the element is visible
      );
    });

    splide.on('moved', index => {
      const isDesktop = window.matchMedia('(min-width: 1280px)').matches;

      if (isDesktop) return;

      this.updateCurrentPage(index);
    });

    splide.on('active', slide => {
      const isDesktop = window.matchMedia('(min-width: 1280px)').matches;

      if (!isDesktop) return;

      const slideIndex = slide.index;
      const calculatedIndex = slideIndex + 2;
      const currentPage = Math.floor(calculatedIndex / 3);

      this.updateCurrentPage(currentPage);
    });

    splide.on('updated', () => {
      const totalPages = Math.ceil(splide.length / (splide.options.perPage ?? 1));
      const index = splide.index >= totalPages ? totalPages - 1 : splide.index;

      this.updateTotalPages(totalPages);
      this.updateCurrentPage(index);
    });

    splide.on('overflow', (isOverflowing: boolean) => {
      splide.options = {
        drag: isOverflowing
      };
    });

    this.splide = splide;
    splide.mount();
  }

  updateCurrentPage(index: number) {
    const currentPage = this.dom.paginationCurrent as HTMLElement;
    if (currentPage) {
      currentPage.textContent = String(index + 1);
    }
  }

  updateTotalPages(total: number) {
    const totalPages = this.dom.paginationTotal as HTMLElement;
    if (totalPages) {
      totalPages.textContent = String(total);
    }
  }

  animateTitle() {
    const { title } = this.dom;

    if (this.isReducedMotion || !title) {
      (title as HTMLElement)?.classList.add('module--reduced-motion');
      return;
    }

    inView(
      title as HTMLElement,
      () => {
        animate(
          title as HTMLElement,
          {
            opacity: [0, 1],
            transform: ['translateY(20px)', 'translateY(0px)']
          },
          {
            duration: 0.5,
            ease: 'easeInOut'
          }
        );
      },
      {
        amount: 0.75
      }
    );
  }
}

export default NewsEventsCardRow;
